import React, { useState, useEffect } from "react";
import {
  Page,
  Layout,
  Card,
  Text,
  BlockStack,
  InlineStack,
  SkeletonBodyText,
  Badge,
} from "@shopify/polaris";
import { TIER_NAMES, TIER_COLORS, TIER_EMOJIS, PROGRAM_NAME, POINTS_UNIT } from "../../shared/types.js";

export default function Dashboard() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadStats();
  }, []);

  async function loadStats() {
    try {
      const res = await fetch("/api/dashboard");
      if (res.ok) {
        setStats(await res.json());
      } else {
        // Use mock data for development
        setStats(getMockStats());
      }
    } catch {
      setStats(getMockStats());
    } finally {
      setLoading(false);
    }
  }

  if (loading) {
    return (
      <Page title="Dashboard">
        <Layout>
          <Layout.Section>
            <Card>
              <SkeletonBodyText lines={5} />
            </Card>
          </Layout.Section>
        </Layout>
      </Page>
    );
  }

  return (
    <Page title="Dashboard" subtitle={`${PROGRAM_NAME} Genel Bakış`}>
      <Layout>
        {/* Top Stats Row */}
        <Layout.Section>
          <InlineStack gap="400" wrap={false}>
            <StatCard
              title="Toplam Uye"
              value={formatNumber(stats.totalMembers)}
              color="#4A90D9"
            />
            <StatCard
              title="Aktif Kupon"
              value={formatNumber(stats.activeCoupons)}
              color="#50C878"
            />
            <StatCard
              title={`Bu Ay Kazanılan ${POINTS_UNIT}`}
              value={formatNumber(stats.monthlyPointsEarned)}
              color="#dbff00"
            />
            <StatCard
              title={`Bu Ay Harcanan ${POINTS_UNIT}`}
              value={formatNumber(stats.monthlyPointsSpent)}
              color="#ff580d"
            />
          </InlineStack>
        </Layout.Section>

        {/* Tier Distribution */}
        <Layout.Section variant="oneHalf">
          <Card>
            <BlockStack gap="400">
              <Text as="h2" variant="headingMd">
                Tier Dagilimi
              </Text>
              {Object.entries(stats.tierDistribution).map(([tier, count]) => (
                <InlineStack key={tier} align="space-between">
                  <InlineStack gap="200" blockAlign="center">
                    <div
                      style={{
                        width: 12,
                        height: 12,
                        borderRadius: "50%",
                        backgroundColor: TIER_COLORS[tier],
                      }}
                    />
                    <Text>{TIER_EMOJIS[tier]} {TIER_NAMES[tier]}</Text>
                  </InlineStack>
                  <InlineStack gap="200" blockAlign="center">
                    <Text variant="bodyMd" fontWeight="bold">
                      {count}
                    </Text>
                    <Text variant="bodySm" tone="subdued">
                      ({stats.totalMembers > 0 ? Math.round((count / stats.totalMembers) * 100) : 0}%)
                    </Text>
                  </InlineStack>
                </InlineStack>
              ))}
            </BlockStack>
          </Card>
        </Layout.Section>

        {/* Coupon Usage */}
        <Layout.Section variant="oneHalf">
          <Card>
            <BlockStack gap="400">
              <Text as="h2" variant="headingMd">
                Kupon Kullanim Orani
              </Text>
              <InlineStack align="space-between">
                <Text>Olusturulan Kupon</Text>
                <Text fontWeight="bold">{stats.totalCouponsCreated}</Text>
              </InlineStack>
              <InlineStack align="space-between">
                <Text>Kullanilan Kupon</Text>
                <Text fontWeight="bold">{stats.totalCouponsUsed}</Text>
              </InlineStack>
              <InlineStack align="space-between">
                <Text>Kullanim Orani</Text>
                <Badge
                  tone={stats.couponUsageRate > 50 ? "success" : "warning"}
                >
                  %{stats.couponUsageRate}
                </Badge>
              </InlineStack>
              <InlineStack align="space-between">
                <Text>Toplam Indirim Tutari</Text>
                <Text fontWeight="bold">
                  {formatCurrency(stats.totalDiscountAmount)} TL
                </Text>
              </InlineStack>
            </BlockStack>
          </Card>
        </Layout.Section>

        {/* Recent Activity */}
        <Layout.Section>
          <Card>
            <BlockStack gap="400">
              <Text as="h2" variant="headingMd">
                Son Aktiviteler
              </Text>
              {stats.recentActivity.length === 0 ? (
                <Text tone="subdued">Henuz aktivite yok</Text>
              ) : (
                stats.recentActivity.map((activity, i) => (
                  <InlineStack key={i} align="space-between">
                    <InlineStack gap="200" blockAlign="center">
                      <Badge tone={getActivityTone(activity.type)}>
                        {activity.type === "points_earned"
                          ? "HP"
                          : activity.type === "tier_upgrade"
                          ? "Tier"
                          : activity.type === "coupon_redeemed"
                          ? "Kupon"
                          : "Rozet"}
                      </Badge>
                      <Text>{activity.description}</Text>
                    </InlineStack>
                    <Text variant="bodySm" tone="subdued">
                      {formatDate(activity.date)}
                    </Text>
                  </InlineStack>
                ))
              )}
            </BlockStack>
          </Card>
        </Layout.Section>
      </Layout>
    </Page>
  );
}

function StatCard({ title, value, color }) {
  return (
    <div
      style={{
        flex: 1,
        backgroundColor: "#fff",
        borderRadius: 8,
        padding: 20,
        borderTop: `4px solid ${color}`,
        boxShadow: "0 1px 3px rgba(0,0,0,0.08)",
      }}
    >
      <Text variant="bodySm" tone="subdued">
        {title}
      </Text>
      <Text variant="headingXl" as="p" fontWeight="bold">
        {value}
      </Text>
    </div>
  );
}

function getActivityTone(type) {
  switch (type) {
    case "points_earned": return "info";
    case "tier_upgrade": return "success";
    case "coupon_redeemed": return "warning";
    case "badge_earned": return "attention";
    default: return undefined;
  }
}

function formatNumber(n) {
  return new Intl.NumberFormat("tr-TR").format(n || 0);
}

function formatCurrency(n) {
  return new Intl.NumberFormat("tr-TR", { minimumFractionDigits: 2 }).format(n || 0);
}

function formatDate(dateStr) {
  if (!dateStr) return "";
  return new Date(dateStr).toLocaleDateString("tr-TR", {
    day: "numeric",
    month: "short",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function getMockStats() {
  return {
    totalMembers: 847,
    activeCoupons: 23,
    monthlyPointsEarned: 12450,
    monthlyPointsSpent: 3200,
    tierDistribution: {
      rookie: 612,
      pro: 189,
      legend: 46,
    },
    totalCouponsCreated: 156,
    totalCouponsUsed: 98,
    couponUsageRate: 63,
    totalDiscountAmount: 24500,
    recentActivity: [
      { type: "points_earned", description: "Ahmet Y. +480 Hobi Puan kazandı", date: "2026-04-14T10:30:00" },
      { type: "tier_upgrade", description: "Mehmet K. Pro seviyeye yükseldi 🏎️", date: "2026-04-14T09:15:00" },
      { type: "coupon_redeemed", description: "Ayşe D. 150 TL kupon oluşturdu", date: "2026-04-13T16:45:00" },
      { type: "badge_earned", description: "Can B. 'Garaj Ustası' 🔧 rozeti kazandı", date: "2026-04-13T14:20:00" },
      { type: "points_earned", description: "Fatma S. +800 Hobi Puan kazandı", date: "2026-04-13T11:00:00" },
    ],
  };
}
